import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { placementsAPI, studentsAPI, feedbackFormsAPI, feedbackTemplatesAPI, placementSettingsAPI, emailAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, ExternalLink, Copy, Trash2, CheckCircle, Clock, Eye, Link2, Archive, ArchiveRestore, Mail, Bell } from 'lucide-react';
import { toast } from 'sonner';

const PlacementFeedback = () => {
  const { placementId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [placement, setPlacement] = useState(null);
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({ assessments_per_placement: 4 });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    template_id: '',
    assessment_number: 1,
    due_date: '',
    chef_name: '',
    chef_email: '',
  });

  useEffect(() => {
    fetchData();
  }, [placementId]);

  const fetchData = async () => {
    try {
      const [formsRes, templatesRes, settingsRes] = await Promise.all([
        feedbackFormsAPI.getAll({ placement_id: placementId }),
        feedbackTemplatesAPI.getAll(),
        placementSettingsAPI.get(),
      ]);
      setForms(formsRes.data);
      setTemplates(templatesRes.data);
      setSettings(settingsRes.data);

      // Get placement details from the forms list or fetch placements
      if (formsRes.data.length > 0) {
        const f = formsRes.data[0];
        setPlacement({
          id: placementId,
          student_name: f.student_name,
          student_number: f.student_number,
          restaurant_name: f.restaurant_name,
        });
      } else {
        // Need to get placement info differently
        const allPlacements = await placementsAPI.getAll();
        const found = allPlacements.data.find(p => p.id === placementId);
        if (found) {
          setPlacement(found);
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async () => {
    if (!createForm.template_id) {
      toast.error('Please select a template');
      return;
    }
    if (!createForm.due_date) {
      toast.error('Please set a due date');
      return;
    }

    try {
      await feedbackFormsAPI.create({
        placement_id: placementId,
        template_id: createForm.template_id,
        assessment_number: createForm.assessment_number,
        due_date: createForm.due_date,
        chef_name: createForm.chef_name,
        chef_email: createForm.chef_email,
      });
      toast.success('Feedback form created');
      setShowCreateModal(false);
      setCreateForm({ template_id: '', assessment_number: forms.length + 1, due_date: '', chef_name: '', chef_email: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create form');
    }
  };

  const handleDeleteForm = async (formId) => {
    try {
      await feedbackFormsAPI.delete(formId);
      toast.success('Form deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  const handleUpdateDueDate = async (formId, newDate) => {
    if (!newDate) return;
    try {
      await feedbackFormsAPI.patch(formId, { due_date: newDate });
      toast.success('Due date updated');
      // Optimistic local update to avoid a flicker while we refetch
      setForms((prev) => prev.map((f) => (f.id === formId ? { ...f, due_date: newDate } : f)));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update due date');
      fetchData(); // resync on failure
    }
  };

  const isArchived = forms.length > 0 && forms.every(f => f.archived);

  const handleArchiveFeedback = async () => {
    try {
      await placementsAPI.archiveFeedback(placementId);
      toast.success('Feedback archived successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive feedback');
    }
  };

  const handleUnarchiveFeedback = async () => {
    try {
      await placementsAPI.unarchiveFeedback(placementId);
      toast.success('Feedback unarchived successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to unarchive feedback');
    }
  };

  const handleSendEmail = async (formId) => {
    try {
      const res = await emailAPI.sendAssessment({ form_ids: [formId], base_url: window.location.origin });
      if (res.data.sent > 0) toast.success('Assessment email sent to chef');
      else toast.error(res.data.errors?.[0]?.error || 'Failed to send email');
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const handleSendReminder = async (formId) => {
    try {
      const res = await emailAPI.sendReminder({ form_ids: [formId], base_url: window.location.origin });
      if (res.data.sent > 0) toast.success('Reminder sent to chef');
      else toast.error(res.data.errors?.[0]?.error || 'Failed to send reminder');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const handleSendAllEmails = async () => {
    const pendingForms = forms.filter(f => f.status === 'pending' && !f.archived);
    if (pendingForms.length === 0) { toast.info('No pending forms to email'); return; }
    try {
      const res = await emailAPI.sendAssessment({ form_ids: pendingForms.map(f => f.id), base_url: window.location.origin });
      toast.success(`Sent ${res.data.sent} emails${res.data.errors?.length ? `, ${res.data.errors.length} failed` : ''}`);
    } catch (error) {
      toast.error('Failed to send emails');
    }
  };

  const copyLink = (accessToken) => {
    const url = `${window.location.origin}/feedback/${accessToken}`;
    const fallbackCopy = () => {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        toast.success('Feedback link copied to clipboard');
      } catch {
        toast.info(`Feedback link: ${url}`, { duration: 10000 });
      }
      document.body.removeChild(textarea);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url).then(
        () => toast.success('Feedback link copied to clipboard'),
        () => fallbackCopy()
      );
    } else {
      fallbackCopy();
    }
  };

  const openNewForm = () => {
    const defaultTemplate = templates.find(t => t.is_default);
    setCreateForm({
      template_id: defaultTemplate?.id || '',
      assessment_number: forms.length + 1,
      due_date: '',
      chef_name: '',
      chef_email: '',
    });
    setShowCreateModal(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="placement-feedback-page" className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/placements')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Placements
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-ica-navy">Placement Feedback</h1>
              {placement && (
                <p className="text-gray-500 text-sm">
                  {placement.student_name} at {placement.restaurant_name}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {forms.some(f => f.status === 'pending' && !f.archived) && (
              <Button
                data-testid="send-all-emails-btn"
                variant="outline"
                onClick={handleSendAllEmails}
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email All Chefs
              </Button>
            )}
            {forms.length > 0 && (
              isArchived ? (
                <Button
                  data-testid="unarchive-feedback-btn"
                  variant="outline"
                  onClick={handleUnarchiveFeedback}
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  <ArchiveRestore className="w-4 h-4 mr-2" />
                  Unarchive
                </Button>
              ) : (
                <Button
                  data-testid="archive-feedback-btn"
                  variant="outline"
                  onClick={handleArchiveFeedback}
                  className="border-amber-500 text-amber-600 hover:bg-amber-50"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Feedback
                </Button>
              )
            )}
            <Button
              data-testid="create-feedback-form-btn"
              onClick={openNewForm}
              
              disabled={templates.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Feedback Form
            </Button>
          </div>
        </div>

        {/* Progress */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-600">Assessment Progress</p>
              <p className="text-sm text-gray-500">
                {forms.filter(f => f.status === 'submitted').length} / {settings.assessments_per_placement} completed
              </p>
            </div>
            <div className="flex gap-2">
              {Array.from({ length: settings.assessments_per_placement }, (_, i) => {
                const form = forms.find(f => f.assessment_number === i + 1);
                return (
                  <div
                    key={i}
                    className={`flex-1 h-2 rounded-full ${
                      form?.status === 'submitted'
                        ? 'bg-emerald-500'
                        : form?.status === 'pending'
                        ? 'bg-amber-400'
                        : 'bg-gray-200'
                    }`}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Archived Banner */}
        {isArchived && (
          <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Archive className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-800 font-medium">This placement's feedback has been archived.</p>
          </div>
        )}

        {/* Forms List */}
        {forms.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-gray-500">No feedback forms created for this placement yet.</p>
              {templates.length === 0 && (
                <p className="text-sm text-gray-400 mt-2">Create a feedback template first.</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {forms.map((form) => (
              <Card key={form.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        form.status === 'submitted'
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-amber-100 text-amber-600'
                      }`}>
                        {form.status === 'submitted' ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <Clock className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          Assessment #{form.assessment_number}
                          <span className="ml-2 text-xs font-normal text-gray-500">
                            ({form.template_name})
                          </span>
                        </p>
                        <div className="flex items-center gap-3 text-sm text-gray-500 flex-wrap">
                          <div className="flex items-center gap-1.5">
                            <span>Due:</span>
                            {form.status === 'submitted' ? (
                              <span className="font-medium text-gray-700">{form.due_date}</span>
                            ) : (
                              <Input
                                type="date"
                                data-testid={`edit-due-date-${form.id}`}
                                defaultValue={form.due_date}
                                onBlur={(e) => {
                                  if (e.target.value && e.target.value !== form.due_date) {
                                    handleUpdateDueDate(form.id, e.target.value);
                                  }
                                }}
                                className="h-7 py-0 px-2 text-xs w-36"
                              />
                            )}
                          </div>
                          {form.chef_name && <span>Chef: {form.chef_name}</span>}
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            form.status === 'submitted'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {form.status === 'submitted' ? 'Submitted' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {form.status === 'submitted' ? (
                        <Button
                          data-testid={`view-submission-${form.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/feedback-view/${form.id}`)}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />
                          View
                        </Button>
                      ) : (
                        <>
                          <Button
                            data-testid={`email-chef-${form.id}`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendEmail(form.id)}
                            title="Email assessment link to chef"
                          >
                            <Mail className="w-3.5 h-3.5 mr-1" />
                            Email
                          </Button>
                          <Button
                            data-testid={`remind-chef-${form.id}`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendReminder(form.id)}
                            title="Send reminder to chef"
                          >
                            <Bell className="w-3.5 h-3.5 mr-1" />
                            Remind
                          </Button>
                          <Button
                            data-testid={`copy-link-${form.id}`}
                            variant="outline"
                            size="sm"
                            onClick={() => copyLink(form.access_token)}
                          >
                            <Link2 className="w-3.5 h-3.5 mr-1" />
                            Copy Link
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/feedback/${form.access_token}`, '_blank')}
                          >
                            <ExternalLink className="w-3.5 h-3.5 mr-1" />
                            Open
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteForm(form.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {form.status === 'pending' && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg flex items-center gap-2 text-sm text-gray-500">
                      <Link2 className="w-3.5 h-3.5" />
                      <span className="truncate">
                        Access Code: <strong>{form.access_code}</strong>
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Form Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-ica-navy">Create Feedback Form</DialogTitle>
            <DialogDescription>
              Generate a new assessment form for the chef to complete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template *</Label>
              <Select
                value={createForm.template_id}
                onValueChange={(val) => setCreateForm({ ...createForm, template_id: val })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} {t.is_default ? '(Default)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assessment Number</Label>
              <Select
                value={createForm.assessment_number.toString()}
                onValueChange={(val) => setCreateForm({ ...createForm, assessment_number: parseInt(val) })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: settings.assessments_per_placement }, (_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      Assessment #{i + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={createForm.due_date}
                onChange={(e) => setCreateForm({ ...createForm, due_date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Chef / Mentor Name</Label>
              <Input
                value={createForm.chef_name}
                onChange={(e) => setCreateForm({ ...createForm, chef_name: e.target.value })}
                placeholder="Optional"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Chef / Mentor Email</Label>
              <Input
                value={createForm.chef_email}
                onChange={(e) => setCreateForm({ ...createForm, chef_email: e.target.value })}
                placeholder="Optional - for notification"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button
              data-testid="confirm-create-form-btn"
              onClick={handleCreateForm}
              
            >
              Create Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PlacementFeedback;
